import { Flex, Box, Text, TransparentButton, Divider, Stack } from "flicket-ui";

import { ReleaseType } from "~graphql/sdk";
import { CreateOrderFn } from "~hooks";
import {
  INormalisedData,
  IReleaseTicket,
  IReleaseZone,
} from "~features/nonseated-reservation/nonseated-reservation.types";
import { ExperienceSalesFooter } from "~features/nonseated-reservation/components/ExperienceSalesFooter/ExperienceSalesFooter";
import { ReservationList } from "~components/reservation/non-seated";
import { FixedRatioImage, Icon } from "~components/common";
import HeaderAlertBanner, {
  useShowHeaderBanner,
} from "~components/common/common.HeaderAlertBanner";
import { useRouter } from "next/router";
import { DefaultTheme, ThemeProps } from "styled-components";
import { ReactNode, useEffect } from "react";
import StickySection from "~components/common/StickySection";
import { CaretLeft } from "phosphor-react";

type NonSeatedProps = {
  data: INormalisedData;
  zones: IReleaseZone[];
  addons: IReleaseTicket[];
  createOrder: CreateOrderFn;
  isMembership: boolean;
  isAdmin: boolean;
  releaseId: string;
  releaseNotes: string;
  allowPublicComp: boolean;
  isResaleRelease: boolean;
  releaseType: ReleaseType;
  requiresAuth: boolean;
};

export const NonSeatedReservationPage = ({
  addons,
  data,
  zones,
  createOrder,
  isMembership = false,
  isAdmin,
  releaseId,
  releaseNotes,
  allowPublicComp,
  isResaleRelease,
  releaseType,
  requiresAuth = false,
}: NonSeatedProps) => {
  const router = useRouter();

  function onClickBack() {
    void router.push("/");
  }

  return (
    <ScrollWindow>
      <Header
        onClickBack={() => {
          void router.push("/");
        }}
      />

      {data.banner && (
        <Box
          width={1}
          px={{ _: 0, sm: 2 }}
          maxW={{ sm: 960 }}
          margin={"auto" as any}
          mb={3}
        >
          <Box position="relative">
            <FixedRatioImage
              image={data.banner}
              alt={data.name}
              ratio={"720 / 288"}
              width={{ _: "100%" }}
              borderRadius={{ sm: "sm" }}
            />
            <TransparentButton
              position="absolute"
              top={2}
              left={2}
              onClick={onClickBack}
              width={40}
              height={40}
              borderRadius="full"
              css={`
                background-color: rgba(255, 255, 255, 0.92);

                &:hover {
                  background-color: ${(p: ThemeProps<DefaultTheme>) =>
                    p.theme.colors.N200};
                }
              `}
            >
              <Icon
                icon={<CaretLeft size={20} weight="bold" />}
                color="N800"
                fontSize={6}
              />
            </TransparentButton>
          </Box>
        </Box>
      )}

      <Box width={1} px={2} maxW={{ sm: 960 }} margin={"auto" as any}>
        <Box pb={8}>
          <Text variant="header.L">{data.name}</Text>
          <Text variant="regular">
            {data.displayDate} • {data.location}
          </Text>

          <Box mt={4}>
            <ZoneTabs zones={zones} />

            <ReservationList
              addons={addons}
              data={data}
              zones={zones}
              createOrder={createOrder}
              isMembership={isMembership}
              releaseNotes={releaseNotes}
              isDigital={data.isDigital}
              allowPublicComp={allowPublicComp}
              isResaleRelease={isResaleRelease}
              releaseType={releaseType}
              requiresAuth={requiresAuth}
            />
          </Box>
        </Box>
      </Box>
      <ExperienceSalesFooter
        id={data.id}
        releaseId={releaseId}
        hideFooterDuringMode={["no-selection"]}
      />
    </ScrollWindow>
  );
};

function ScrollWindow({ children }: { children: ReactNode }) {
  return (
    <Box
      id="scroll-window"
      width={1}
      overflowY="auto"
      h="100dvh"
      position="relative"
    >
      {children}
    </Box>
  );
}

const TABS_HEIGHT = 48;

function ZoneTabs({ zones }: { zones: IReleaseZone[] }) {
  const router = useRouter();
  const { bannerHeight, isBannerVisible } = useShowHeaderBanner();

  useEffect(() => {
    if (router.query.zoneId) {
      const offset = (isBannerVisible ? bannerHeight : 0) + TABS_HEIGHT + 32;
      const scrollWindowEl = document.getElementById("scroll-window");

      scrollWindowEl.scrollTo({
        behavior: "smooth",
        top:
          scrollWindowEl.scrollTop +
          document
            .getElementById(router.query.zoneId as string)
            ?.getBoundingClientRect().top -
          offset,
      });
    }
  }, [router.query.zoneId]);

  return (
    <StickySection
      options={{
        root: document.body,
        rootMargin: `${isBannerVisible ? bannerHeight : 0}px 0px 0px 0px`,
        threshold: 1,
      }}
      stickyCSS={`
        position: fixed;
        top: ${isBannerVisible ? bannerHeight : 0}px;
        left: 0;
        right: 0;
        z-index: 999;
      `}
    >
      {({ isSticky }) => (
        <Box
          mb={4}
          backgroundColor="white"
          boxShadow={isSticky ? "card" : undefined}
        >
          <Box
            maxW={{ sm: isSticky ? 960 : "none" }}
            margin={"auto" as any}
            px={{ _: isSticky ? 2 : 0 }}
          >
            <Stack
              direction="horizontal"
              gap={2}
              w="100%"
              overflowX="auto"
              pr={2}
              position={"relative"}
              zIndex={1}
            >
              {zones.map((zone, i) => {
                return (
                  <Flex
                    key={zone.id}
                    onClick={() => {
                      void router.replace({
                        pathname: router.pathname,
                        query: {
                          ...router.query,
                          zoneId: zone.id,
                        },
                      });
                    }}
                    alignItems="flex-end"
                    flexWrap="nowrap"
                  >
                    <Text
                      variant="header.XS"
                      cursor="pointer"
                      css={`
                        text-wrap: nowrap;
                        &:hover {
                          border-color: ${(p: ThemeProps<DefaultTheme>) =>
                            p.theme.colors.N800};
                        }
                      `}
                      py={2}
                      borderBottom="3px solid"
                      borderColor={
                        (!router.query.zoneId && i === 0) ||
                        router.query.zoneId === zone.id
                          ? "N800"
                          : "transparent"
                      }
                    >
                      {zone.name}
                    </Text>
                  </Flex>
                );
              })}
            </Stack>
          </Box>
          <Divider
            css={`
              margin-top: -1px;
            `}
          />
        </Box>
      )}
    </StickySection>
  );
}

function Header({ onClickBack }: { onClickBack: () => void }) {
  const { isBannerVisible, bannerHeight } = useShowHeaderBanner();

  return (
    <>
      <Box
        zIndex="banner"
        css={`
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
        `}
      >
        <HeaderAlertBanner />
      </Box>

      <Box pb={{ sm: 3 }} />
    </>
  );
}
